import { LabImageAuthoringData_LabGraderType as LabImageGraderType } from '__generated__/graphql-types';

import { org_coursera_workspace_authoring_WorkspaceImageApplicationType as WorkspaceImageApplicationType } from 'bundles/author-lab-manager/hooks/__generated__/globalTypes';

import _t from 'i18n!nls/author-lab-manager';

/**
 * [AI-generated]
 *
 * @description Constants defining the available workspace image application types
 */
export const ImageApplicationTypes = {
  ...WorkspaceImageApplicationType,
} as const;

/**
 * [AI-generated]
 *
 * @description Type representing the available workspace image application types
 */
export type ImageApplicationType = (typeof ImageApplicationTypes)[keyof typeof ImageApplicationTypes];

// GraderType is used for backend, AutograderTypes is used for frontend
/**
 * [AI-generated]
 *
 * @description Constants defining the available autograder types, including a NONE option for frontend use
 */
export const AutograderTypes = {
  ...LabImageGraderType,
  NONE: 'NONE',
} as const;

/**
 * [AI-generated]
 *
 * @description Type representing the available autograder types
 */
export type AutograderType = (typeof AutograderTypes)[keyof typeof AutograderTypes];

/**
 * [AI-generated]
 *
 * @description Constants defining the available base image customization types
 */
export const BaseImageCustomizationTypes = {
  PYTHON_PACKAGES: 'PYTHON_PACKAGES',
  R_PACKAGES: 'R_PACKAGES',
  DOCKERFILE_COMMANDS: 'DOCKERFILE_COMMANDS',
} as const;

/**
 * [AI-generated]
 *
 * @description Default polling interval in milliseconds for workspace operations
 */
export const DEFAULT_POLLING_INTERVAL = 5000;

export const API_BEFORE_SEND = 'API_BEFORE_SEND';
export const API_IN_PROGRESS = 'API_IN_PROGRESS';
export const API_ERROR = 'API_ERROR';

// https://partner.coursera.help/hc/en-us/articles/360025397911
export const PARTNER_HELP_LINK_ID_LABS = '360025397911';
// https://partner.coursera.help/hc/en-us/articles/000001662
export const PARTNER_HELP_LINK_ID_IMAGE_CREATION = '000001662';
// https://partner.coursera.help/hc/articles/360038168492
export const PARTNER_HELP_LINK_ID_IMAGE_EDITING = '360038168492';
export const PARTNER_HELP_LINK_ANCHOR_IMAGE_DETAILS = 'image_details';
export const PARTNER_HELP_LINK_ANCHOR_ADD_IMAGE = 'add_image';
export const PARTNER_HELP_LINK_ANCHOR_ADD_LAB = 'add_lab';

export const getPartnerHelpLinkPropsForImageApplicationType = (imageApplicationType?: ImageApplicationType) => {
  const result = {
    articleId: PARTNER_HELP_LINK_ID_IMAGE_CREATION,
    anchor: '',
  };

  if (imageApplicationType === ImageApplicationTypes.JUPYTER_NOTEBOOK) {
    result.anchor = 'add_jn';
  } else if (imageApplicationType === ImageApplicationTypes.VSCODE) {
    result.anchor = 'add_vscode';
  } else if (imageApplicationType === ImageApplicationTypes.RSTUDIO) {
    result.anchor = 'add_rstudio';
  } else if (imageApplicationType === ImageApplicationTypes.CUSTOM) {
    result.anchor = 'add_custom_image';
  }

  return result;
};

// https://partner.coursera.help/hc/en-us/articles/360043136952
export const PARTNER_HELP_LINK_ID_NBGRADER = '360043136952';
// https://partner.coursera.help/hc/articles/4402955530011
export const PARTNER_HELP_LINK_ID_VSCODE_GRADER = '4402955530011';

// https://partner.coursera.help/hc/en-us/articles/360038168492
export const PARTNER_HELP_LINK_ID_MANAGE_IMAGES_AND_LABS = '360038168492';
export const PARTNER_HELP_LINK_ANCHOR_LINKED_LABS = 'linked_labs';

// https://partner.coursera.help/hc/en-us/articles/360037660172#enable_lab_sharing
export const PARTNER_HELP_LINK_ID_LAB_SHARING_AUTHOR = '360037660172';
export const PARTNER_HELP_LINK_ANCHOR_ENABLE_SHARING = 'enable_lab_sharing';

// https://partner.coursera.help/hc/en-us/articles/360044758731-Solving-Common-Issues-with-Coursera-Labs
export const LEARNER_HELP_LINK_ID_LAB_SHARING_LEARNER = '360044758731-Solving-Common-Issues-with-Coursera-Labs';

/**
 * [AI-generated]
 *
 * @description Constants defining the available lab manager menu options
 */
export const LabManagerMenuStrings = {
  EDIT_IMAGE: 'Edit image',
  VIEW_CONTAINER_LOGS_FOR_IMAGE: 'View container logs for image',
  REBUILD_IMAGE: 'Rebuild image',
  VERSION_HISTORY: 'Version history',
  DELETE_IMAGE: 'Delete image',
  EDIT_LAB: 'Edit lab',
  VIEW_CONTAINER_LOGS_FOR_LAB: 'View container logs for lab',
  DELETE_LAB: 'Delete lab',
} as const;

/**
 * [AI-generated]
 *
 * @description Type representing the available lab manager menu options
 */
export type LabManagerMenuOptions = (typeof LabManagerMenuStrings)[keyof typeof LabManagerMenuStrings];

/**
 * [AI-generated]
 *
 * @description Helper function to get translated strings for lab manager menu options
 * @param key - The menu option key to get the translation for
 * @returns The translated string for the given menu option
 */
export const getTranslation = (key: LabManagerMenuOptions) => {
  const translations = {
    [LabManagerMenuStrings.EDIT_IMAGE]: _t('Edit image'),
    [LabManagerMenuStrings.VIEW_CONTAINER_LOGS_FOR_IMAGE]: _t('View container logs for image'),
    [LabManagerMenuStrings.REBUILD_IMAGE]: _t('Rebuild image'),
    [LabManagerMenuStrings.VERSION_HISTORY]: _t('Version history'),
    [LabManagerMenuStrings.DELETE_IMAGE]: _t('Delete image'),
    [LabManagerMenuStrings.EDIT_LAB]: _t('Edit lab'),
    [LabManagerMenuStrings.VIEW_CONTAINER_LOGS_FOR_LAB]: _t('View container logs for lab'),
    [LabManagerMenuStrings.DELETE_LAB]: _t('Delete lab'),
  };
  return translations[key];
};
